import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

const API_BASE = process.env.REACT_APP_API_URL;

const emergencyContacts = [
  { name: 'Police', number: '100', emoji: '🚓' },
  { name: 'Ambulance', number: '108', emoji: '🚑' },
  { name: 'Fire Brigade', number: '101', emoji: '🚒' },
  { name: 'Women Helpline', number: '1091', emoji: '👩' },
  { name: 'Senior Helpline', number: '14567', emoji: '🧓' }
];

const formatTimeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hours ago`;
  return new Date(dateStr).toLocaleDateString('en-IN');
};

const SOS = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertSent, setAlertSent] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [location, setLocation] = useState('');
  const [activeAlertId, setActiveAlertId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/sos`, { headers });
      setAlerts(res.data.alerts || []);
    } catch (err) {
      console.error('Fetch SOS alerts error:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
    // Try to get location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { latitude, longitude } = pos.coords;
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await res.json();
            const addr = data.address;
            const locStr = [addr.road, addr.suburb || addr.neighbourhood, addr.city || addr.town]
              .filter(Boolean).join(', ');
            setLocation(locStr || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
          } catch {
            // silently fail
          }
        },
        () => {}
      );
    }
  }, [fetchAlerts]);

  // Countdown timer
  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      triggerSOS();
      return;
    }
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const startSOS = () => {
    setCountdown(3);
  };

  const cancelCountdown = () => {
    setCountdown(null);
    toast('SOS cancelled', { icon: '✋' });
  };

  const triggerSOS = async () => {
    setCountdown(null);
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/api/sos`, { location }, { headers });
      setAlertSent(true);
      setActiveAlertId(res.data.alert?.id);
      setAlerts([res.data.alert, ...alerts]);
      toast.success('🚨 SOS Alert sent! Help is on the way.', { duration: 5000 });
    } catch (err) {
      toast.error('Failed to send SOS. Call emergency services directly!');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!activeAlertId) return;
    try {
      await axios.put(`${API_BASE}/api/sos/${activeAlertId}/resolve`, {}, { headers });
      setAlertSent(false);
      setActiveAlertId(null);
      setAlerts(alerts.map(a => a.id === activeAlertId ? { ...a, status: 'resolved' } : a));
      toast.success('SOS alert resolved. Stay safe! 💚');
    } catch (err) {
      toast.error('Failed to resolve alert');
    }
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{
        background: alertSent ? 'linear-gradient(135deg, #D32F2F 0%, #F44336 100%)' : 'linear-gradient(135deg, #B71C1C 0%, #D32F2F 100%)',
        padding: '16px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            onClick={() => navigate('/home')}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', color: 'white', cursor: 'pointer', fontSize: '20px' }}
          >
            ←
          </button>
          <h2 style={{ color: 'white', fontSize: '20px' }}>🆘 Emergency Help</h2>
          <div style={{ width: '40px' }} />
        </div>
        {alertSent && (
          <div style={{
            marginTop: '12px',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '12px',
            padding: '10px',
            fontSize: '14px'
          }}>
            🚨 ALERT ACTIVE - Help is being notified
          </div>
        )}
      </div>

      <div className="page-content">
        {/* Main SOS Button Area */}
        <div style={{ textAlign: 'center', padding: '24px 0 20px' }}>
          {countdown !== null ? (
            // Countdown
            <div>
              <div style={{
                width: '160px',
                height: '160px',
                borderRadius: '50%',
                background: '#FFF3E0',
                border: '6px solid #FF9800',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '64px',
                fontWeight: '900',
                color: '#FF9800'
              }}>
                {countdown}
              </div>
              <p style={{ fontSize: '17px', fontWeight: '600', color: '#2D2D2D', marginBottom: '16px' }}>
                Sending SOS in {countdown} second{countdown !== 1 ? 's' : ''}...
              </p>
              <button
                className="btn btn-ghost"
                onClick={cancelCountdown}
                style={{ maxWidth: '200px', margin: '0 auto', borderColor: '#FF9800', color: '#FF9800' }}
              >
                ✋ Cancel
              </button>
            </div>
          ) : alertSent ? (
            // Alert sent state
            <div>
              <div style={{
                width: '160px',
                height: '160px',
                borderRadius: '50%',
                background: '#FFEBEE',
                border: '6px solid #F44336',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                animation: 'pulse-ring 1.5s infinite'
              }}>
                <span style={{ fontSize: '56px' }}>🚨</span>
              </div>
              <p style={{ fontSize: '18px', fontWeight: '700', color: '#D32F2F', marginBottom: '8px' }}>
                SOS Alert Active
              </p>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                Family & community have been notified
              </p>
              <button
                className="btn btn-secondary"
                onClick={handleResolve}
                style={{ maxWidth: '260px', margin: '0 auto' }}
              >
                ✅ I'm Safe - Resolve Alert
              </button>
            </div>
          ) : (
            // Normal SOS button
            <div>
              <button
                className="pulse-btn"
                onClick={startSOS}
                disabled={loading}
                style={{
                  width: '160px',
                  height: '160px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
                  border: '6px solid #FFCDD2',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  cursor: 'pointer',
                  boxShadow: '0 8px 32px rgba(244,67,54,0.5)',
                  fontFamily: "'Hind', 'Poppins', sans-serif"
                }}
              >
                <span style={{ fontSize: '48px' }}>🆘</span>
                <span style={{ color: 'white', fontSize: '18px', fontWeight: '800', marginTop: '4px' }}>SOS</span>
              </button>

              <p style={{ fontSize: '16px', fontWeight: '600', color: '#2D2D2D', marginBottom: '6px' }}>
                Press to Alert Family & Community
              </p>
              <p style={{ fontSize: '13px', color: '#999' }}>
                Will countdown 3 seconds before sending
              </p>

              {location && (
                <div style={{
                  marginTop: '12px',
                  padding: '8px 14px',
                  background: '#E8F5E9',
                  borderRadius: '10px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>📍</span>
                  <span style={{ fontSize: '13px', color: '#2E7D32' }}>{location}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Emergency Contacts */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '12px' }}>📞 Emergency Contacts</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {emergencyContacts.map((contact) => (
              <a
                key={contact.number}
                href={`tel:${contact.number}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  background: 'white',
                  border: '2px solid #FFCDD2',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  transition: 'all 0.2s'
                }}
              >
                <span style={{ fontSize: '22px' }}>{contact.emoji}</span>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#2D2D2D' }}>{contact.name}</div>
                  <div style={{ fontSize: '16px', fontWeight: '800', color: '#F44336' }}>{contact.number}</div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Recent SOS Alerts */}
        {alerts.length > 0 && (
          <div>
            <h3 style={{ marginBottom: '12px' }}>Recent Alerts</h3>
            {alerts.slice(0, 5).map((alert) => (
              <div key={alert.id} className="card" style={{
                marginBottom: '8px',
                borderLeft: `4px solid ${alert.status === 'active' ? '#F44336' : '#4CAF50'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#2D2D2D', marginBottom: '3px' }}>
                      {alert.is_mine ? '👤 You' : `👤 ${alert.user_name}`}
                    </div>
                    {alert.location && (
                      <div style={{ fontSize: '13px', color: '#666', marginBottom: '3px' }}>
                        📍 {alert.location}
                      </div>
                    )}
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      {formatTimeAgo(alert.created_at)}
                    </div>
                  </div>
                  <span style={{
                    background: alert.status === 'active' ? '#FFEBEE' : '#E8F5E9',
                    color: alert.status === 'active' ? '#C62828' : '#2E7D32',
                    fontSize: '12px',
                    fontWeight: '700',
                    padding: '4px 10px',
                    borderRadius: '10px'
                  }}>
                    {alert.status === 'active' ? '🔴 Active' : '🟢 Resolved'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default SOS;
